import {
  CancellationReason,
  Charge,
  ChargeEventObject,
  ChargeStatus,
  Maybe,
  RefundReason,
  SubscriptionFieldsFragment
} from 'api/generated';
import {formatPhone} from 'lib/formaters';
import {config} from '../stage.config';
import {ChargePaymentMethods} from '../types/ChargePaymentMethods';

export const getChargeDescription = (charge?: Charge | ChargeEventObject | null) => {
  if (!charge) return '';
  if (charge.description) return charge.description;
  if (charge.shop?.name) return `${charge.shop.name} - #${charge.orderId}`;
  return `#${charge.orderId}`;
};

export const getChargeCustomer = (
  charge: Charge | ChargeEventObject | SubscriptionFieldsFragment
) => {
  const customer = charge.customer || {};
  if (customer.email) return customer.email;
  if (customer.phone) return formatPhone(customer.phone, null, false);
  return formatPhone(charge.paymentMethod?.bizum?.phoneNumber, null, false);
};

export const getRefundReasonLabels = (): Record<string, string> => ({
  [RefundReason.DUPLICATED]: 'Duplicated',
  [RefundReason.FRAUDULENT]: 'Fraudulent',
  [RefundReason.REQUESTED_BY_CUSTOMER]: 'Requested by customer',
  [RefundReason.ORDER_CANCELED]: 'Order canceled'
});

export const getCancellationReasonLabels = (): Record<string, string> => ({
  [CancellationReason.DUPLICATED]: 'Duplicated',
  [CancellationReason.FRAUDULENT]: 'Fraudulent',
  [CancellationReason.REQUESTED_BY_CUSTOMER]: 'Requested by customer',
  [CancellationReason.ORDER_CANCELED]: 'Order canceled'
});

export const canBeRefunded = (charge?: Maybe<Charge>) => {
  if (!charge) return false;
  return [ChargeStatus.SUCCEEDED, ChargeStatus.PARTIALLY_REFUNDED].includes(charge.status);
};

export const canBeCaptured = (charge?: Maybe<Charge>) => {
  if (!charge) return false;
  return [ChargeStatus.AUTHORIZED].includes(charge.status);
};

export const canBeCanceled = (charge?: Maybe<Charge>) => {
  if (!charge) return false;
  return [ChargeStatus.AUTHORIZED, ChargeStatus.PENDING].includes(charge.status);
};

export const canSendPaymentLink = (charge?: Maybe<Charge>) => {
  if (!charge) return false;
  return [ChargeStatus.PENDING].includes(charge.status);
};

export const canSendPaymentReceipt = (charge?: Maybe<Charge>) => {
  if (!charge) return false;
  return [
    ChargeStatus.SUCCEEDED,
    ChargeStatus.REFUNDED,
    ChargeStatus.PARTIALLY_REFUNDED,
    ChargeStatus.PAID_OUT
  ].includes(charge.status);
};

export const getChargePaymentMethods = (chargeId: string): Promise<ChargePaymentMethods> => {
  return fetch(config.moneiApi + '/payment-methods?paymentId=' + chargeId)
    .then((result) => result.json())
    .then((data) => data);
};
